import { type ActionCreator, on, type ReducerTypes } from '@ngrx/store';
import { produce } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';

import { CommandRegistry } from '../../ai';

import {
  applyChanges,
  executeAICommands,
  undoAICommands,
  updatePageAiDescription,
} from './ai.actions';
import { type AIState, initialAIState } from './ai.state';

/**
 * The engine slice as the AI handlers write it. The composition types every added slice as
 * optional, `ai` included, because it cannot promise which ones are registered.
 */
type AIEngineState = EngineStateBase & Partial<AIState>;

export const aiReducerTypes: readonly ReducerTypes<AIEngineState, ActionCreator[]>[] = [
  on(executeAICommands, (state, { payload }) => {
    const newState = { ...state, ...initialAIState };

    return produce(newState, (draft) => {
      try {
        for (const commandPayload of payload) {
          const executor = CommandRegistry.getExecutor(commandPayload);
          executor.executeMutably(draft, commandPayload);
        }
      } catch (e) {
        console.error(`Error executing AI commands`, e);
        draft.ai.error = e instanceof Error ? e.message : 'Unknown error';
      }
    });
  }),

  on(undoAICommands, (state, payload): AIEngineState => {
    return {
      ...state,
      ...payload.state,
    };
  }),
  on(applyChanges, (state, payload): AIEngineState => {
    return {
      ...state,
      ...payload.state,
    };
  }),
  on(updatePageAiDescription, (state, { pageId, aiDescription }): AIEngineState => {
    return {
      ...state,
      pages: state.pages.map((page) => {
        if (page.id === pageId) {
          return {
            ...page,
            aiDescription,
          };
        }

        return page;
      }),
    };
  }),
];
